import type { BookLessonSelection } from "@/components/lessonGenerator/BookLessonPicker";
import { toCountInput, toSlideCount } from "@/components/lessonGenerator/countRules";
import type { DataSource, GeneratorSection } from "@/components/lessonGenerator/types";
import { useFragment } from "@/generated";
import {
  CreateGenerationPresetDocument,
  DeleteGenerationPresetDocument,
  type GenerationPresetFieldsFragment,
  GenerationPresetFieldsFragmentDoc,
  type GenerationPresetInput,
  GenerationPresetsDocument,
  GetSlidesByIdsDocument,
  type LessonDataSourceEnum,
  MarkGenerationPresetUsedDocument,
  SlideFieldsFragmentDoc,
  UpdateGenerationPresetDocument,
} from "@/generated/graphql";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import type { SlideFields } from "@/types/Slide";
import type { SlideTemplate } from "@ai-teach/types";
import { useMutation, useQuery } from "@apollo/client/react";
import { type SavedSection, useSectionPresets } from "../sectionPresets/useSectionPresets";

export interface GenerationPreset {
  id: string;
  name: string;
  target: BookLessonSelection;
  sections: GeneratorSection[];
  skippedSectionCount: number;
}

type PresetSection = GenerationPresetFieldsFragment["sections"][number];

/** A section the preset stored on its own, either never linked or whose saved section is gone. */
function toSection(section: PresetSection, slide: SlideFields): GeneratorSection {
  return {
    id: section.id,
    ...(section.name ? { name: section.name } : {}),
    template: section.template as SlideTemplate,
    referenceSlide: {
      id: slide.id,
      name: slide.name,
      template: slide.template,
      tagIds: slide.tagIds,
      slide,
    },
    context: section.context as DataSource[],
    ...(section.sectionScopes.length > 0 ? { sectionScopes: section.sectionScopes } : {}),
    ...(section.subLists.length > 0 ? { subLists: section.subLists } : {}),
    ...(section.count ? { count: toSlideCount(section.count) } : {}),
    ...(section.parent
      ? {
          parent: {
            template: section.parent.template as SlideTemplate,
            tagId: section.parent.tagId,
            each: section.parent.each,
          },
        }
      : {}),
    prompt: section.prompt,
    randomizeChoicePositions: section.randomizeChoicePositions,
    generateSentence: section.generateSentence,
    slideName: section.slideName ?? getTemplateLabel(section.template as SlideTemplate),
    numberSlides: section.numberSlides,
  };
}

/** The template's settings win, except the slide naming each preset keeps for itself. */
function fromSavedSection(section: PresetSection, saved: SavedSection): GeneratorSection {
  return {
    id: section.id,
    sectionPresetId: saved.id,
    name: saved.name,
    template: saved.template,
    referenceSlide: saved.referenceSlide,
    context: saved.context,
    ...(saved.sectionScopes?.length ? { sectionScopes: saved.sectionScopes } : {}),
    ...(saved.subLists?.length ? { subLists: saved.subLists } : {}),
    ...(saved.count ? { count: saved.count } : {}),
    ...(saved.parent ? { parent: saved.parent } : {}),
    prompt: saved.prompt,
    randomizeChoicePositions: saved.randomizeChoicePositions,
    generateSentence: saved.generateSentence,
    slideName: section.slideName ?? getTemplateLabel(saved.template),
    numberSlides: section.numberSlides,
  };
}

export function useGenerationPresets() {
  const { data, loading, refetch } = useQuery(GenerationPresetsDocument, {
    fetchPolicy: "cache-and-network",
  });

  const stored = (data?.generationPresets ?? []).map((one) =>
    useFragment(GenerationPresetFieldsFragmentDoc, one)
  );
  const referenceSlideIds = [
    ...new Set(stored.flatMap((preset) => preset.sections.map((one) => one.referenceSlideId))),
  ];

  const { data: slidesData, loading: slidesLoading } = useQuery(GetSlidesByIdsDocument, {
    variables: { ids: referenceSlideIds },
    skip: referenceSlideIds.length === 0,
    fetchPolicy: "cache-first",
  });

  const slidesById = new Map(
    (slidesData?.getSlidesByIds ?? [])
      .map((slide) => useFragment(SlideFieldsFragmentDoc, slide))
      .map((slide) => [slide.id, slide as SlideFields])
  );

  const { sections: savedSections, loading: savedLoading } = useSectionPresets();
  const savedById = new Map(savedSections.map((one) => [one.id, one]));

  const presets: GenerationPreset[] = stored.map((preset) => {
    const sections = preset.sections.flatMap((section) => {
      const saved = section.sectionPresetId ? savedById.get(section.sectionPresetId) : undefined;
      if (saved) return [fromSavedSection(section, saved)];
      const slide = slidesById.get(section.referenceSlideId);
      return slide ? [toSection(section, slide)] : [];
    });

    return {
      id: preset.id,
      name: preset.name,
      target: {
        seriesSlug: preset.seriesSlug ?? null,
        levelId: preset.levelId ?? null,
        componentId: preset.componentId ?? null,
        lesson: null,
      },
      sections,
      skippedSectionCount: preset.sections.length - sections.length,
    };
  });

  const [create] = useMutation(CreateGenerationPresetDocument);
  const [update] = useMutation(UpdateGenerationPresetDocument);
  const [remove] = useMutation(DeleteGenerationPresetDocument);
  const [markUsed] = useMutation(MarkGenerationPresetUsedDocument);

  return {
    presets,
    loading: loading || slidesLoading || savedLoading,
    savePreset: async (input: GenerationPresetInput) => {
      const { data: created } = await create({ variables: { input } });
      await refetch();
      const preset = created?.createGenerationPreset;
      return preset ? useFragment(GenerationPresetFieldsFragmentDoc, preset).id : null;
    },
    updatePreset: async (id: string, input: GenerationPresetInput) => {
      await update({ variables: { id, input } });
      await refetch();
    },
    deletePreset: async (id: string) => {
      await remove({ variables: { id } });
      await refetch();
    },
    markPresetUsed: async (id: string) => {
      await markUsed({ variables: { id } });
    },
  };
}

export function toPresetInput(
  name: string,
  target: BookLessonSelection,
  sections: GeneratorSection[]
): GenerationPresetInput {
  return {
    name,
    seriesSlug: target.seriesSlug,
    levelId: target.levelId,
    componentId: target.componentId,
    sections: sections.map((section) => ({
      id: section.id,
      sectionPresetId: section.sectionPresetId ?? null,
      name: section.name ?? null,
      template: section.template,
      referenceSlideId: section.referenceSlide.id,
      context: section.context as LessonDataSourceEnum[],
      sectionScopes: section.sectionScopes ?? [],
      subLists: section.subLists ?? [],
      count: section.count ? toCountInput(section.count) : null,
      parent: section.parent ?? null,
      prompt: section.prompt,
      randomizeChoicePositions: section.randomizeChoicePositions ?? false,
      generateSentence: section.generateSentence ?? false,
      slideName: section.slideName,
      numberSlides: section.numberSlides,
    })),
  };
}
